"use client";
import { InfoTooltip } from "@/ui/index";
import { LoadingSpinner } from "@gettactic/components/src/loadings/LoadingSpinner/LoadingSpinner";
import { useCustomParkingVerbiageOnWeb as useCustomParkingVerbiage } from "@/lib/hooks/useCustomParkingVerbiage";
import {
  RESERVE_TEXTS,
  SaveReservationData,
  SaveReservationDataUsers
} from "@/features/reserve/utils/utils-reserve";
import { IResource, Resource, ResourceType } from "@gettactic/api";
import { useAreas } from "@gettactic/hooks";
import { useMemo } from "react";
import { FieldErrors, UseFormReturn } from "react-hook-form";
import { Controller } from "react-hook-form";
import { UnifiedSelect, SelectItem } from "@cmp-src/form/UnifiedSelect";
import {
  Select,
  SelectTrigger,
  SelectItem as RadixSelectItem,
  SelectContent,
  SelectValue
} from "@cmp-src/primitives/Select";
import { useIsMobile } from "@cmp-src/hooks/use-mobile";

export type NarrowResourceTypes = "meeting_room" | "workspace" | "parking";

type ReserveFieldResourcesListProps = {
  resourceType: ResourceType;
  form: UseFormReturn<SaveReservationData>;
  resourcesList: Resource[] | null | undefined;
  defaultValue: Resource | null;
  officeId: string;
  errors: FieldErrors<SaveReservationData>;
};

export function isOverCapacity(
  users: SaveReservationDataUsers,
  resource: IResource | null = null
) {
  return !!users && !!resource && users.length > resource.available_slots;
}

type TooltipOverCapacityProps = {
  users: SaveReservationDataUsers;
  resource: IResource;
  entity: NarrowResourceTypes;
};

export function TooltipOverCapacity({
  users,
  resource,
  entity
}: TooltipOverCapacityProps) {
  let visualEntity: NarrowResourceTypes | "locker" = entity;
  const customParkingVerbiage = useCustomParkingVerbiage();
  if (customParkingVerbiage && entity === "parking") {
    visualEntity = "locker";
  }
  return !!resource && !!users ? (
    <div className="mt-2 flex max-w-[155px] items-center rounded-xl bg-yellow-50 px-2 py-1">
      <span className="text-yellow-800">Over Capacity</span>
      <InfoTooltip testId="capacity-tooltip">
        <div className="w-48 font-semibold">
          {RESERVE_TEXTS.overCapacityTitle[visualEntity]}
        </div>
        <div className="text-sm">
          {RESERVE_TEXTS.overCapacityTotalSlots[visualEntity]}:&nbsp;
          {resource.available_slots}
        </div>
        <div className="text-sm">
          {RESERVE_TEXTS.overCapacityUsersLength[visualEntity]}:&nbsp;
          <span className="text-primary" data-testid="users-length">
            {users.length}
          </span>
        </div>
      </InfoTooltip>
    </div>
  ) : null;
}

export function ReserveFieldResourcesList({
  resourcesList,
  defaultValue: def,
  form,
  resourceType: resType,
  officeId,
  errors
}: ReserveFieldResourcesListProps) {
  const isMobile = useIsMobile();
  const customParkingVerbiage = useCustomParkingVerbiage();

  const resourceType: NarrowResourceTypes = useMemo(() => {
    if (resType === "parking_lot") {
      return "parking";
    }
    if (resType === "parking_space") {
      return "parking";
    }
    if (["meeting_room", "workspace"].includes(resType)) {
      return resType as NarrowResourceTypes;
    }
    return "workspace";
  }, [resType]);

  const { areas } = useAreas(officeId);
  const users = form.watch("users");
  const resource = form.watch("resource");
  const showTooltipOverCapacity = isOverCapacity(users, resource);
  type ResourceOption = Resource & { label: string; value: string };

  const options = useMemo(
    () =>
      (resourcesList ?? []).map((res) => ({
        ...res,
        label: res.name,
        value: res.id
      })),
    [resourcesList]
  );

  const items: SelectItem<ResourceOption>[] = useMemo(
    () =>
      options.map((res) => ({
        id: res.id,
        name: res.name,
        disabled: res.type === "area",
        data: res
      })),
    [options]
  );

  const defaultValue = useMemo(
    () => options.find((res) => res.id === def?.id),
    [def, options]
  );

  const showEmpty = options.length === 0 && !defaultValue;
  if (!resourcesList) {
    return (
      <LoadingSpinner color="text-secondary-foreground" />
    );
  }
  const isParking = resourceType === "parking";

  let fieldPlaceholder = RESERVE_TEXTS.selectResource[resourceType];
  let parkingEmptyMessage = "parking spaces";
  if (isParking && customParkingVerbiage) {
    fieldPlaceholder = `Select ${customParkingVerbiage}`;
    parkingEmptyMessage = customParkingVerbiage;
  }

  return (
    <>
      {showEmpty ? (
        <p className="text-primary">{` No ${isParking ? parkingEmptyMessage : resourceType === "meeting_room" ? "rooms" : "desks"} available to reserve!`}</p>
      ) : (
        <>
          <Controller
            control={form.control}
            name={"resource"}
            rules={{ required: true }}
            defaultValue={defaultValue}
            render={({ field: { onChange, value } }) => {
              const getValueContent = (props: {
                data: (typeof options)[0];
              }) => {
                return (
                  <>
                    {props.data.name}
                    {props.data.available_slots > 0 && (
                      <span className="ml-2 rounded-full bg-green-100 px-2.5 py-0.5 font-medium text-green-800 text-xs">
                        {props.data.available_slots}
                      </span>
                    )}
                    {areas &&
                    props.data.parent_id &&
                    areas.byId[props.data.parent_id] ? (
                      <span className="ml-1 rounded-md bg-neutral-100 px-2.5 py-0.5 text-xs">
                        {areas.byId[props.data.parent_id].name}
                      </span>
                    ) : null}
                    {Array.isArray(props.data.tags) &&
                      props.data.tags.length > 0 &&
                      props.data.tags.map((tag) => {
                        return (
                          <span
                            key={tag.id}
                            className="ml-1 rounded-md bg-neutral-200 px-2.5 py-0.5 text-xs"
                          >
                            {tag.name}
                          </span>
                        );
                      })}
                  </>
                );
              };

              const selectedItem = value
                ? (items.find((item) => item.id === value.id) ?? null)
                : null;

              return isMobile ? (
                <Select
                  value={value?.id}
                  onValueChange={(val) => {
                    onChange(options.find((res) => res.id === val));
                  }}
                >
                  <SelectTrigger variant={"gray"} className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {options?.map((option) => (
                      <RadixSelectItem
                        key={option.id}
                        value={option.id}
                        disabled={option.type === "area"}
                      >
                        {getValueContent({ data: option })}
                      </RadixSelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <UnifiedSelect
                  items={items}
                  value={selectedItem}
                  onSelect={(item) => {
                    if (!item || Array.isArray(item)) {
                      onChange(null);
                      return;
                    }
                    onChange(item.data);
                  }}
                  placeholder={fieldPlaceholder}
                  searchable
                  triggerClassName="resource-select w-full !border-0 bg-neutral-50"
                  renderItem={(item) =>
                    getValueContent({ data: item.data as ResourceOption })
                  }
                  renderValue={(selected) => {
                    if (!selected || Array.isArray(selected)) return null;
                    return getValueContent({
                      data: selected.data as ResourceOption
                    });
                  }}
                />
              );
            }}
          />

          {/*@ts-ignore*/}
          {errors.resource && errors.resource.type === "required" && (
            <p className="pl-1 text-primary">{fieldPlaceholder}</p>
          )}
          {/*@ts-ignore*/}
          {errors.resource && errors.resource.type !== "required" && (
            <p className="text-primary">
              Hmm, something went wrong. Can you try again?
            </p>
          )}
          {showTooltipOverCapacity && (
            <TooltipOverCapacity
              users={users}
              resource={resource}
              entity={resourceType}
            />
          )}
        </>
      )}
    </>
  );
}
